import { gql } from '@apollo/client'

export const PACKAGING_GUIDE_FULL = gql`
  query PackagingGuideFull($sellingPeriodId: String) {
    packagingGuideContext(sellingPeriodId: $sellingPeriodId) {
      teamId
      teamName
      teamLead {
        firstName
        lastName
        phone
        email
        username
      }
      noEndedSellingPeriod
      sellingPeriod {
        id
        name
        start
        end
        addressDeliveryName
        address
        postalCode
        city
        phone
        assortment {
          name
        }
        primaryTrackingLinks
        secondaryTrackingLinks
      }
      perProduct {
        productId
        productTitle
        productImage
        quantity
      }
      perMember {
        referalCode
        username
        firstName
        lastName
        phone
        lines {
          productId
          productTitle
          quantity
          soldQuantity
          bonusQuantity
        }
      }
      packagingState
    }
  }
`

export const UPDATE_PACKAGING_GUIDE_STATE = gql`
  mutation UpdatePackagingGuideState(
    $sellingPeriodId: String!
    $packagingState: JSON!
  ) {
    updatePackagingGuideState(
      sellingPeriodId: $sellingPeriodId
      packagingState: $packagingState
    )
  }
`

export const GENERATE_PACKAGING_PDF = gql`
  mutation GeneratePackagingPdf(
    $sellingPeriodId: String!
    $kind: String!
    $fileName: String!
    $teamName: String!
    $periodLabel: String
    $rows: [PackagingProductPdfInput!]
    $members: [PackagingMemberPdfInput!]
  ) {
    generatePackagingPdf(
      sellingPeriodId: $sellingPeriodId
      kind: $kind
      fileName: $fileName
      teamName: $teamName
      periodLabel: $periodLabel
      rows: $rows
      members: $members
    ) {
      fileName
      mimeType
      base64
    }
  }
`

/**
 * Stored on SellingPeriod.packagingState (JSON).
 *
 * This is intentionally flexible and versioned to support iterative UX changes.
 */
export type PackagingStateV2 = {
  version: 2
  /** Step 1 (bags): user marked names written. */
  bagsWritten?: {
    done?: boolean
    writtenMemberCodes?: Record<string, true>
  }
  /**
   * Pack per product.
   * - receivedByProduct: what arrived / what user says they received.
   * - packedByProduct: which members were packed for that product (boolean = packed as expected).
   */
  perProduct?: {
    receivedByProduct?: Record<string, number>
    packedByProduct?: Record<string, Record<string, true>>
    /** Packed quantity per seller (partial packing); overrides boolean flags when set. */
    packedQtyByProduct?: Record<string, Record<string, number>>
  }
  /**
   * Pack per member.
   * - packedByMember: which products are packed for that member (boolean = packed as expected).
   * - overridesByMemberProduct: when user packs a different amount than expected.
   */
  perMember?: {
    packedByMember?: Record<string, Record<string, true>>
    overridesByMemberProduct?: Record<string, Record<string, number>>
  }
  /** Free-form notes for the summary/discrepancies. */
  summary?: {
    message?: string
    sentAt?: string
    /** Snapshot taken when summary is sent; used for admin queue filtering. */
    submittedHadDiscrepancies?: boolean
    /** Snapshot of missing products (only those with tooFew > 0) when summary is sent. */
    submittedMissingProductIds?: string[]
    /** Admin queue: mark handled. */
    discrepancyAdminHandledAt?: string
    discrepancyAdminHandledBy?: string
    /** Snapshot when sammanfattning skickas (för admin). */
    submittedByUsername?: string
    submittedByFirstName?: string
    submittedByLastName?: string
    submittedByPhone?: string
    missingProductsAction?: 'refund' | 'redelivery'
    missingProductsAddress?: {
      name?: string
      address?: string
      postalCode?: string
      city?: string
      phone?: string
    }
  }
}

export type PackagingStateV1 = {
  receivedByProduct?: Record<string, number>
  memberNotes?: Record<string, string>
}

export function coercePackagingState(
  raw: unknown
): PackagingStateV2 | PackagingStateV1 | {} {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {}
  }
  const any = raw as { version?: unknown }
  if (any.version === 2) {
    return raw as PackagingStateV2
  }
  return raw as PackagingStateV1
}
